import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { usePermissions } from '@/common/hooks/usePermissions';
import { useDelayedSkeleton } from '@/common/hooks';
import { PERMISSIONS } from '@/common/config/permissions';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  getWhatsAppFlows,
  getWhatsAppFlow,
  createWhatsAppFlow,
  updateWhatsAppFlow,
  deleteWhatsAppFlow,
  getMarketplaceIndustries,
  getMarketplaceWorkflows,
  getMarketplaceWorkflowDetail,
  downloadJsonFile,
} from '../api';
import FlowBuilder from '../components/FlowBuilder';
import { transformMarketplaceWorkflowToFlowState } from '../utils/marketplaceAdapter';

import { TagChips } from '../../crm/components/TagChips';
import { collectAutomationTags } from '../utils/tags';
import { ResponsiveBadgeList } from '../../components/ResponsiveBadgeList';
import {
  Button,
  ConfirmDeleteModal,
  CustomSelect,
  SearchInput,
  SkeletonText,
  Switch,
  TableChip,
} from '@/components';
import { parseApiError } from '@/common/utils';
import { parseKeywords } from '../utils/keywords';
import { isCompleteFlowDetail, serializeSavedFlow } from '../utils/flowJson';
import { DashboardPage, PillTabs } from '../../components/PremiumDesign';
import { DownloadIcon, PlusIcon } from '../../components/Icons';
import { WhatsAppSettingsDrawer } from '../../components/Integrations/WhatsAppSettingsDrawer';
import editIcon from '../../assets/editIcon.png';
// import trashIcon from '../../assets/trashIcon.png';
import eyeIcon from '../../assets/eye.png';
import { PermissionGuard, V2Table } from '@/common/components';

const FLOW_STATUS_TABS = [
  { id: 'all', label: 'All' },
  { id: 'published', label: 'Published' },
  { id: 'draft', label: 'Drafts' },
  // TODO: Add marketplace tab when admin panel is ready
  // { id: 'marketplace', label: 'Marketplace' },
];

function FlowStatusBadge({ flow }) {
  if (flow.is_draft) {
    return <TableChip tone="warning">Draft</TableChip>;
  }
  if (flow.is_active) {
    return <TableChip tone="success">Active</TableChip>;
  }
  return <TableChip tone="neutral">Inactive</TableChip>;
}

/** Shows keyword pills for a flow in the list table. */
function FlowKeywordBadges({ flow }) {
  const chips = parseKeywords(flow.keywords || flow.keyword || '');
  if (chips.length === 0) {
    return <span className="text-text-muted text-xs">—</span>;
  }
  return (
    <ResponsiveBadgeList
      items={chips}
      gap={4}
      className="gap-1 min-w-0 max-w-full whitespace-nowrap"
      renderItem={(chip) => (
        <div
          key={chip}
          className="relative group/chip inline-flex shrink-0 flex-shrink-0 hover:z-30"
          style={{ flexShrink: 0 }}
        >
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-[6px] text-[10px] font-medium bg-[#43B02A]/10 text-[#2e8020] border border-[#43B02A]/20 whitespace-nowrap shrink-0 flex-shrink-0 cursor-default"
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {chip}
          </span>

          {/* Instant hover tooltip */}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/chip:flex flex-col items-center z-[100] pointer-events-none transition-all duration-150">
            <div className="bg-[#1F2241] text-white text-[11px] sm:text-xs font-medium py-1 px-2.5 rounded shadow-xl whitespace-nowrap max-w-[260px] truncate">
              {chip}
            </div>
            <div className="w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-[#1F2241]" />
          </div>
        </div>
      )}
      renderMore={(hiddenCount, hiddenChips) => {
        const hiddenText = (hiddenChips || []).join(', ');
        return (
          <div
            className="relative group/chip inline-flex shrink-0 flex-shrink-0 hover:z-30"
            style={{ flexShrink: 0 }}
          >
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-[6px] text-[10px] font-medium bg-black/5 text-text-muted border border-border-default cursor-pointer whitespace-nowrap shrink-0 flex-shrink-0"
              style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              +{hiddenCount}
            </span>

            {/* Instant hover tooltip for extra hidden keywords */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/chip:flex flex-col items-center z-[100] pointer-events-none transition-all duration-150">
              <div className="bg-[#1F2241] text-white text-[11px] sm:text-xs font-medium py-1 px-2.5 rounded shadow-xl whitespace-normal max-w-[260px] text-center">
                {hiddenText}
              </div>
              <div className="w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-[#1F2241]" />
            </div>
          </div>
        );
      }}
    />
  );
}

export function WhatsAppFlowsPage() {
  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();
  const queryClient = useQueryClient();
  const { can } = usePermissions();

  const isOwner = (() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.role === 'owner';
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return false;
  })();

  const canCreateFlow = can(PERMISSIONS.WA_FLOWS.CREATE);
  const canUpdateFlow = can(PERMISSIONS.WA_FLOWS.UPDATE);
  // const canDeleteFlow = can(PERMISSIONS.WA_FLOWS.DELETE);
  const canViewFlow = can(PERMISSIONS.WA_FLOWS.VIEW);

  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [loadingMarketplaceWorkflowId, setLoadingMarketplaceWorkflowId] =
    useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'builder'
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [builderKey, setBuilderKey] = useState('new');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [flowToDelete, setFlowToDelete] = useState(null);
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const [exportingFlowId, setExportingFlowId] = useState(null);
  const listScrollTopRef = useRef(0);

  const { data: marketplaceIndustries = [] } = useQuery({
    queryKey: ['marketplace-industries'],
    queryFn: () => getMarketplaceIndustries(),
    enabled: statusFilter === 'marketplace',
    staleTime: 60_000,
  });

  const { data: marketplaceWorkflows = [], isLoading: isMarketplaceLoading } =
    useQuery({
      queryKey: [
        'marketplace-workflows',
        selectedIndustry,
        debouncedSearchQuery,
      ],
      queryFn: () =>
        getMarketplaceWorkflows({
          industry_id: selectedIndustry || undefined,
          q: debouncedSearchQuery || undefined,
        }),
      enabled: statusFilter === 'marketplace',
      staleTime: 30_000,
    });

  const marketplaceList = Array.isArray(marketplaceWorkflows)
    ? marketplaceWorkflows
    : marketplaceWorkflows?.results || [];

  const handleUseMarketplaceWorkflow = async (workflowId) => {
    try {
      setLoadingMarketplaceWorkflowId(workflowId);
      const detail = await getMarketplaceWorkflowDetail(workflowId);
      const prefilledFlow = transformMarketplaceWorkflowToFlowState(detail);
      openBuilder(prefilledFlow);
    } catch (err) {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to load marketplace automation template');
    } finally {
      setLoadingMarketplaceWorkflowId(null);
    }
  };

  const captureListScroll = () => {
    const el = document.getElementById('dashboard-scroll-container');
    listScrollTopRef.current = el?.scrollTop ?? 0;
  };

  const openBuilder = (flow = null) => {
    captureListScroll();
    setSelectedFlow(flow);
    setBuilderKey(flow?.id ? `flow-${flow.id}` : `new-${Date.now()}`);
    setViewMode('builder');
  };

  const returnToList = () => {
    if (statusFilter === 'marketplace') {
      setStatusFilter('all');
    }
    setViewMode('list');
    setSelectedFlow(null);
  };

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const {
    data: flowsData,
    isLoading: isInitialLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ['whatsapp-flows', companyId, debouncedSearchQuery, statusFilter],
    queryFn: () => {
      const params = {};
      if (debouncedSearchQuery) params.search = debouncedSearchQuery;
      if (statusFilter === 'published') params.status = 'published';
      if (statusFilter === 'draft') params.status = 'draft';
      return getWhatsAppFlows(companyId, params);
    },
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && flowsData !== undefined,
    300,
    500,
  );
  const isLoading = isInitialLoading || showDelayedSkeleton;

  useEffect(() => {
    if (viewMode !== 'list' || isLoading) return;
    const el = document.getElementById('dashboard-scroll-container');
    if (!el) return;
    const top = listScrollTopRef.current;
    requestAnimationFrame(() => {
      el.scrollTop = top;
    });
  }, [viewMode, isLoading, flowsData]);

  useEffect(() => {
    if (isError) {
      const { message } = parseApiError(error);
      toast.error(message || 'Failed to fetch flows.');
    }
  }, [isError, error]);

  const flowList =
    flowsData?.results || (Array.isArray(flowsData) ? flowsData : []);

  const { data: allFlowsData } = useQuery({
    queryKey: ['whatsapp-flows', companyId, 'all-active-count'],
    queryFn: () => getWhatsAppFlows(companyId),
    enabled: !!companyId,
    staleTime: 30_000,
  });

  const allFlowsList =
    allFlowsData?.results ||
    (Array.isArray(allFlowsData) ? allFlowsData : null) ||
    flowList;

  const MAX_ACTIVE_FLOWS = 10;
  const activeFlowsCount = allFlowsList.filter(
    (f) => !f.is_draft && f.is_active,
  ).length;

  // Client-side keyword filter: supplement server-side name search so that
  // queries like "pricing" surface flows whose keywords contain "pricing".
  const filteredFlows = debouncedSearchQuery
    ? flowList.filter((flow) => {
        const q = debouncedSearchQuery.trim().toLowerCase();
        const kws = parseKeywords(flow.keywords || flow.keyword || '');
        return kws.some((k) => k.includes(q));
      }).length > 0
      ? flowList // server already returned name matches; let keyword filter supplement
      : flowList
    : flowList;

  const {
    data: flowDetail,
    isLoading: isFlowDetailLoading,
    isError: isFlowDetailError,
    error: flowDetailError,
  } = useQuery({
    queryKey: ['whatsapp-flow', companyId, selectedFlow?.id],
    queryFn: () => getWhatsAppFlow({ companyId, flowId: selectedFlow.id }),
    enabled: viewMode === 'builder' && !!companyId && !!selectedFlow?.id,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!isFlowDetailError) return;
    const { message } = parseApiError(flowDetailError);
    toast.error(message || 'Failed to load automation.');
  }, [isFlowDetailError, flowDetailError]);

  const createMutation = useMutation({
    mutationFn: ({ payload }) =>
      createWhatsAppFlow({ companyId, data: payload }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-flows', companyId],
      });
      if (data?.id) {
        queryClient.setQueryData(['whatsapp-flow', companyId, data.id], data);
      }
      if (variables.asDraft) {
        toast.success('Draft saved');
        setSelectedFlow((prev) => ({ ...(prev || {}), ...data }));
        return;
      }
      toast.success('Automation published');
      returnToList();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to save automation. Please try again.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ flowId, payload }) =>
      updateWhatsAppFlow({ companyId, flowId, data: payload }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-flows', companyId],
      });
      if (data?.id) {
        queryClient.setQueryData(['whatsapp-flow', companyId, data.id], data);
      }
      if (variables.asDraft) {
        toast.success('Draft saved');
        setSelectedFlow((prev) => ({ ...(prev || {}), ...data }));
        return;
      }
      toast.success('Automation published');
      returnToList();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to update automation. Please try again.');
    },
  });

  const flowsQueryKey = [
    'whatsapp-flows',
    companyId,
    debouncedSearchQuery,
    statusFilter,
  ];

  const toggleActiveMutation = useMutation({
    mutationFn: ({ flowId, payload }) =>
      updateWhatsAppFlow({ companyId, flowId, data: payload }),
    onMutate: async ({ flowId, payload }) => {
      await queryClient.cancelQueries({
        queryKey: flowsQueryKey,
      });
      const previousFlows = queryClient.getQueryData(flowsQueryKey);

      queryClient.setQueryData(flowsQueryKey, (old) => {
        if (!old) return old;
        const results = Array.isArray(old) ? old : old.results;
        const newResults = results.map((flow) =>
          flow.id === flowId ? { ...flow, ...payload } : flow,
        );
        return Array.isArray(old)
          ? newResults
          : { ...old, results: newResults };
      });

      queryClient.setQueryData(
        ['whatsapp-flows', companyId, 'all-active-count'],
        (old) => {
          if (!old) return old;
          const results = Array.isArray(old) ? old : old.results;
          if (!results) return old;
          const newResults = results.map((flow) =>
            flow.id === flowId ? { ...flow, ...payload } : flow,
          );
          return Array.isArray(old)
            ? newResults
            : { ...old, results: newResults };
        },
      );

      return { previousFlows };
    },
    onError: (err, newFlow, context) => {
      queryClient.setQueryData(flowsQueryKey, context.previousFlows);
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-flows', companyId, 'all-active-count'],
      });
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to update flow status.');
    },
    onSuccess: (data, variables) => {
      const status = variables.payload.is_active ? 'activated' : 'deactivated';
      toast.success(`Flow ${status} successfully`);
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-flows', companyId, 'all-active-count'],
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (flowId) => deleteWhatsAppFlow({ companyId, flowId }),
    onSuccess: () => {
      toast.success('Flow deleted successfully');
      queryClient.invalidateQueries(['whatsapp-flows', companyId]);
      setFlowToDelete(null);
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete flow.');
      setFlowToDelete(null);
    },
  });

  const handleSaveFlow = (flowData, { asDraft } = {}) => {
    const isNewFlow = !selectedFlow?.id;
    if (isNewFlow && !canCreateFlow) {
      toast.error('Access denied. You do not have permission to create flows.');
      return;
    }
    if (!isNewFlow && !canUpdateFlow) {
      toast.error('Access denied. You do not have permission to update flows.');
      return;
    }

    if (!asDraft) {
      const currentActiveCount = allFlowsList.filter(
        (f) => !f.is_draft && f.is_active && f.id !== selectedFlow?.id,
      ).length;
      if (currentActiveCount >= MAX_ACTIVE_FLOWS) {
        toast.error(
          `Maximum ${MAX_ACTIVE_FLOWS} active automations allowed. Please save as draft or deactivate another automation first.`,
        );
        return;
      }
    }

    if (selectedFlow?.id) {
      updateMutation.mutate({
        flowId: selectedFlow.id,
        payload: flowData,
        asDraft: Boolean(asDraft),
      });
    } else {
      createMutation.mutate({
        payload: flowData,
        asDraft: Boolean(asDraft),
      });
    }
  };

  // const handleDelete = (e, id) => {
  //   e.stopPropagation();
  //   if (!canDeleteFlow) {
  //     toast.error('Access denied. You do not have permission to delete flows.');
  //     return;
  //   }
  //   setFlowToDelete(id);
  // };

  const handleToggleActive = (flow) => {
    if (flow.is_draft) return;
    if (!canUpdateFlow) {
      toast.error('Access denied. You do not have permission to update flows.');
      return;
    }

    if (!flow.is_active) {
      const currentActiveCount = allFlowsList.filter(
        (f) => !f.is_draft && f.is_active && f.id !== flow.id,
      ).length;
      if (currentActiveCount >= MAX_ACTIVE_FLOWS) {
        toast.error(
          `Maximum ${MAX_ACTIVE_FLOWS} active automations allowed. Please deactivate another automation before activating this one.`,
        );
        return;
      }
    }

    toggleActiveMutation.mutate({
      flowId: flow.id,
      payload: { is_active: !flow.is_active },
    });
  };

  const handleExportFlow = async (flow) => {
    if (!flow?.id) return;
    setExportingFlowId(flow.id);
    try {
      const cached = queryClient.getQueryData([
        'whatsapp-flow',
        companyId,
        flow.id,
      ]);
      const detail = isCompleteFlowDetail(cached)
        ? cached
        : await getWhatsAppFlow({ companyId, flowId: flow.id });
      if (!isCompleteFlowDetail(detail)) {
        toast.error('Could not load the full flow. Export cancelled.');
        return;
      }
      downloadJsonFile(
        serializeSavedFlow(detail),
        detail.name || flow.name || 'flow',
      );
      toast.success('Flow exported');
    } catch (err) {
      const { message } = parseApiError(err);
      toast.error(message || err.message || 'Failed to export flow.');
    } finally {
      setExportingFlowId(null);
    }
  };

  if (viewMode === 'builder') {
    const isNewFlow = !selectedFlow?.id;
    const canEditBuilder = isNewFlow ? canCreateFlow : canUpdateFlow;
    const canSaveFlow = isNewFlow ? canCreateFlow : canUpdateFlow;
    const waitingForDetail = !isNewFlow && isFlowDetailLoading && !flowDetail;
    const builderFlow = selectedFlow?.id
      ? flowDetail || (isFlowDetailError ? selectedFlow : undefined)
      : selectedFlow;

    if (waitingForDetail || (selectedFlow?.id && !builderFlow)) {
      return (
        <div className="absolute inset-0 flex items-center justify-center min-h-0 overflow-hidden">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      );
    }

    // Pin builder to the dashboard content box so Edit Step form height
    // can never stretch <main> or leave blank space below the canvas.
    return (
      <div className="absolute inset-0 min-h-0 overflow-hidden flex flex-col">
        <FlowBuilder
          key={builderKey}
          initialFlow={builderFlow}
          onSave={handleSaveFlow}
          companyId={companyId}
          isSaving={createMutation.isPending || updateMutation.isPending}
          canEdit={canEditBuilder}
          canSave={canSaveFlow}
          onCancel={returnToList}
        />
      </div>
    );
  }

  const marketplaceColumns = [
    {
      id: 'name',
      label: 'Name',
      defaultWidth: 240,
      minWidth: 160,
      resizable: true,
      skeleton: <SkeletonText width="w-32" height="h-4" />,
      cell: (flow) => (
        <span className="truncate block font-medium" title={flow.name}>
          {flow.name}
        </span>
      ),
    },
    {
      id: 'keywords',
      label: 'Keywords',
      defaultWidth: 220,
      minWidth: 140,
      resizable: true,
      skeleton: <SkeletonText width="w-24" height="h-4" />,
      cell: (flow) => <FlowKeywordBadges flow={flow} />,
    },
    {
      id: 'industry',
      label: 'Industry',
      defaultWidth: 140,
      minWidth: 110,
      align: 'center',
      resizable: true,
      skeleton: <SkeletonText width="w-20" height="h-4" className="mx-auto" />,
      cell: (flow) =>
        flow.industry_name ? (
          <TableChip tone="info">{flow.industry_name}</TableChip>
        ) : (
          <span className="text-text-muted text-xs">—</span>
        ),
    },
    {
      id: 'use',
      label: 'Action',
      defaultWidth: 120,
      minWidth: 100,
      align: 'center',
      resizable: false,
      stopPropagation: true,
      skeleton: <SkeletonText width="w-16" height="h-8" className="mx-auto" />,
      cell: (flow) => (
        <Button
          variant="primary"
          size="sm"
          loading={loadingMarketplaceWorkflowId === flow.id}
          disabled={Boolean(loadingMarketplaceWorkflowId)}
          onClick={() => handleUseMarketplaceWorkflow(flow.id)}
          className="!h-7 !py-0 !px-3 !text-xs inline-flex items-center justify-center"
        >
          Use
        </Button>
      ),
    },
  ];

  const flowColumns = [
    {
      id: 'name',
      label: 'Name',
      defaultWidth: 260,
      minWidth: 180,
      resizable: true,
      skeleton: <SkeletonText width="w-32" height="h-4" />,
      cell: (flow) => (
        <div className="flex flex-col gap-1.5 min-w-0">
          <span className="truncate" title={flow.name}>
            {flow.name}
          </span>
          <TagChips tags={collectAutomationTags(flow)} hideEmpty />
        </div>
      ),
    },
    {
      id: 'keywords',
      label: 'Keywords',
      defaultWidth: 200,
      minWidth: 140,
      resizable: true,
      skeleton: <SkeletonText width="w-24" height="h-4" />,
      cell: (flow) => <FlowKeywordBadges flow={flow} />,
    },
    {
      id: 'default',
      label: 'Default',
      defaultWidth: 110,
      minWidth: 90,
      align: 'center',
      resizable: false,
      skeleton: <SkeletonText width="w-16" height="h-5" className="mx-auto" />,
      cell: (flow) => (
        <TableChip tone={flow.is_default ? 'success' : 'neutral'}>
          {flow.is_default ? 'Yes' : 'No'}
        </TableChip>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      defaultWidth: 120,
      minWidth: 100,
      align: 'center',
      resizable: false,
      skeleton: <SkeletonText width="w-16" height="h-5" className="mx-auto" />,
      cell: (flow) => <FlowStatusBadge flow={flow} />,
    },
    {
      id: 'active',
      label: 'Active',
      defaultWidth: 100,
      minWidth: 88,
      align: 'center',
      resizable: false,
      stopPropagation: true,
      skeleton: <SkeletonText width="w-10" height="h-5" className="mx-auto" />,
      cell: (flow) => {
        const isDraft = Boolean(flow.is_draft);
        return (
          <span
            className="inline-flex"
            title={
              isDraft
                ? 'Publish flow to activate'
                : !flow.is_active && activeFlowsCount >= MAX_ACTIVE_FLOWS
                  ? `Maximum ${MAX_ACTIVE_FLOWS} active automations reached`
                  : undefined
            }
          >
            <Switch
              checked={!isDraft && flow.is_active}
              onChange={() => handleToggleActive(flow)}
              disabled={
                isDraft ||
                !isOwner ||
                !canUpdateFlow ||
                toggleActiveMutation.isPending
              }
              aria-label={
                isDraft ? 'Publish flow to activate' : 'Toggle flow active'
              }
            />
          </span>
        );
      },
    },
    {
      id: 'action',
      label: 'Action',
      defaultWidth: 140,
      minWidth: 120,
      align: 'center',
      resizable: false,
      stopPropagation: true,
      skeleton: <SkeletonText width="w-16" height="h-5" className="mx-auto" />,
      cell: (flow) => (
        <div className="flex items-center justify-center gap-2">
          {!canUpdateFlow && canViewFlow && (
            <button
              type="button"
              onClick={() => openBuilder(flow)}
              className="p-1.5 text-text-muted hover:text-blue-400 hover:bg-white/10 rounded-button transition-all focus:outline-none shrink-0"
              title="View Automation"
            >
              <img
                src={eyeIcon}
                alt="View"
                className="w-5 h-5 object-contain shrink-0"
              />
            </button>
          )}
          {canUpdateFlow && (
            <button
              type="button"
              onClick={() => openBuilder(flow)}
              className="p-1.5 text-text-muted hover:text-amber-400 hover:bg-white/10 rounded-button transition-all focus:outline-none shrink-0"
              title="Edit Automation"
            >
              <img
                src={editIcon}
                alt=""
                className="w-5 h-5 object-contain shrink-0"
              />
            </button>
          )}
          {canViewFlow && (
            <button
              type="button"
              onClick={() => handleExportFlow(flow)}
              disabled={exportingFlowId === flow.id}
              className="p-1.5 text-text-muted hover:text-primary hover:bg-white/10 rounded-button transition-all focus:outline-none shrink-0 disabled:opacity-50"
              title="Export Flow (JSON)"
            >
              {exportingFlowId === flow.id ? (
                <span className="block w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              ) : (
                <DownloadIcon className="w-5 h-5" />
              )}
            </button>
          )}
        </div>
      ),
    },
  ];

  const flowsFiltered =
    Boolean(debouncedSearchQuery) ||
    (statusFilter === 'marketplace' && Boolean(selectedIndustry)) ||
    (statusFilter !== 'all' && statusFilter !== 'marketplace');

  const clearFlowFilters = () => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setSelectedIndustry('');
    if (statusFilter !== 'marketplace') setStatusFilter('all');
  };

  const flowEmpty = flowsFiltered ? (
    <div className="flex flex-col items-center gap-3">
      <span>No matching results</span>
      <button
        type="button"
        onClick={clearFlowFilters}
        className="text-sm font-semibold text-primary hover:text-primary-hover"
      >
        Clear Filters
      </button>
    </div>
  ) : (
    <div className="flex flex-col items-center gap-3">
      <span>
        No automations found. Click &quot;Create Automation&quot; to get
        started.
      </span>
      {canCreateFlow ? (
        <Button
          variant="primary"
          onClick={() => openBuilder()}
          className="h-10 max-w-full !px-3 !text-sm"
          icon={<PlusIcon />}
        >
          Create Automation
        </Button>
      ) : null}
    </div>
  );

  return (
    <DashboardPage className="h-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flows-page flex flex-col w-full mx-auto flex-1 min-h-0 gap-4 overflow-hidden">
        {/* Top Control Bar */}
        <div className="flows-toolbar flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
          <div className="flex flex-wrap items-center gap-3 flex-grow max-w-2xl">
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by Name"
              className="flex-grow max-w-md"
            />
            {statusFilter === 'marketplace' && (
              <CustomSelect
                value={selectedIndustry}
                onChange={(val) => setSelectedIndustry(val)}
                options={[
                  { value: '', label: 'All Industries' },
                  ...marketplaceIndustries.map((ind) => ({
                    value: String(ind.id),
                    label: ind.name,
                  })),
                ]}
                placeholder="All Industries"
                className="w-48 shrink-0"
                buttonClassName="!h-[40px] w-full flex items-center justify-between rounded-xl bg-white border border-border-default px-3 text-sm text-text-primary focus:outline-none cursor-pointer"
              />
            )}
          </div>

          <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto justify-start sm:justify-end shrink-0">
            <Button
              variant="secondary"
              onClick={() => setSettingsDrawerOpen(true)}
              className="h-[40px] !px-2 sm:!px-4 bg-white/[0.04] border border-border-default hover:bg-white/[0.08] !text-xs sm:!text-sm justify-center w-full sm:w-auto overflow-hidden min-w-0"
            >
              <span className="truncate">WhatsApp Settings</span>
            </Button>
            {statusFilter !== 'marketplace' && (
              <PermissionGuard permission={PERMISSIONS.WA_FLOWS.CREATE}>
                <Button
                  variant="primary"
                  onClick={() => openBuilder()}
                  className="h-[40px] !px-2 sm:!px-4 !text-xs sm:!text-sm justify-center w-full sm:w-auto overflow-hidden min-w-0"
                  icon={<PlusIcon />}
                >
                  <span className="truncate">
                    Create<span className="hidden sm:inline"> Automation</span>
                  </span>
                </Button>
              </PermissionGuard>
            )}
          </div>
        </div>

        {/* Tab Bar */}
        <div className="flows-tabs shrink-0">
          <PillTabs
            items={FLOW_STATUS_TABS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </div>

        {/* Table Area */}
        <div className="flows-table w-full flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="w-full min-w-0 flex-1 min-h-0 flex flex-col">
            {statusFilter === 'marketplace' ? (
              <V2Table
                tight
                columns={marketplaceColumns}
                data={marketplaceList}
                rowKey="id"
                storageKey="marketplace_automations_table_column_widths"
                loading={isMarketplaceLoading}
                loadingRowCount={6}
                empty={
                  flowsFiltered
                    ? flowEmpty
                    : 'No marketplace automations found for the selected filter.'
                }
              />
            ) : (
              <V2Table
                tight
                columns={flowColumns}
                data={filteredFlows}
                rowKey="id"
                storageKey="automations_table_column_widths"
                loading={isLoading || !flowsData}
                loadingRowCount={10}
                onRowClick={(flow) => openBuilder(flow)}
                empty={flowEmpty}
              />
            )}
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={!!flowToDelete}
        title="Delete Flow"
        description="Are you sure you want to permanently delete this flow?"
        onConfirm={() => deleteMutation.mutate(flowToDelete)}
        onCancel={() => setFlowToDelete(null)}
        isPending={deleteMutation.isPending}
      />

      <WhatsAppSettingsDrawer
        companyId={companyId}
        isOpen={settingsDrawerOpen}
        onClose={() => setSettingsDrawerOpen(false)}
      />
    </DashboardPage>
  );
}

export default WhatsAppFlowsPage;
